import isEmpty from 'lodash/isEmpty';
import isNil from 'lodash/isNil';
import { useMemo, useSyncExternalStore } from 'react';
import { useIntl } from 'react-intl';

import { Command } from '@ui/components/base/command';
import { Popover, PopoverContent } from '@ui/components/base/popover';
import { TemplateTagCommandList } from '@ui/components/editor/TemplateTagCommandList';
import type { TemplateTagSuggestionBridge } from '@ui/components/editor/templateTagSuggestion';
import { groupTemplateTags } from '@ui/components/editor/templateTagUtils';
import { keepEditorFocus } from '@ui/components/editor/useEditorFocusReturn';

interface TemplateTagSuggestionMenuProps {
  bridge: TemplateTagSuggestionBridge;
}

const TemplateTagSuggestionMenu = ({ bridge }: TemplateTagSuggestionMenuProps) => {
  const { formatMessage } = useIntl();
  const state = useSyncExternalStore(bridge.subscribe, bridge.getState);
  const clientRect = state?.clientRect;
  const anchor = useMemo(() => ({ getBoundingClientRect: () => clientRect?.() ?? new DOMRect() }), [clientRect]);
  const groups = useMemo(() => groupTemplateTags(state?.items ?? []), [state?.items]);

  if (isNil(state) || isEmpty(state.items)) {
    return null;
  }

  return (
    <Popover
      open
      onOpenChange={(open: boolean) => {
        if (!open) {
          bridge.dismiss();
        }
      }}
    >
      <PopoverContent
        data-slot="template-tag-suggestions"
        anchor={anchor}
        align="start"
        side="bottom"
        initialFocus={false}
        finalFocus={false}
        className="w-64 p-0"
        onMouseDown={keepEditorFocus}
      >
        <Command
          shouldFilter={false}
          value={state.items[state.highlightedIndex]?.token ?? ''}
          onValueChange={bridge.highlight}
        >
          <TemplateTagCommandList
            label={formatMessage({ id: 'richTextEditor.templateTagSuggestions' })}
            groups={groups}
            onSelect={bridge.pick}
          />
        </Command>
      </PopoverContent>
    </Popover>
  );
};

export { TemplateTagSuggestionMenu };
